import React, { useEffect, useState } from 'react';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';

/* The Templates "Custom Search" multi-value paste box — mirrors the Quotes
 * CustomSearchDialog. Paste many values (one per line or comma-separated), pick a
 * field, and match any of them. Emits a one-slot blob `field~~=~~v1,v2,v3~~|||`
 * that the backend turns into a match over those values — an exact `IN` for most
 * fields, with the exceptions noted below. SEARCH_FIELDS maps to the backend
 * allowlist (templates.py `_SEARCH_COLUMNS`), which also decides each field's match
 * mode; three are NOT plain exact matches: `deliver_to` matches as a prefix
 * (`LIKE 'val%'`, V1 parity for location names); `pronumber`/`masterpro` match
 * bare-or-"T" forms, so a bare "110" also finds T110; and `quotenumber` is numeric —
 * the backend drops non-numeric values, so a search whose values are all
 * non-numeric matches nothing. No "Search Ended" toggle — templates use the
 * deleted/unapproved toggles on the table instead. */

const SEARCH_FIELDS: { field: string; label: string }[] = [
  { field: 'pronumber', label: 'Record Id / Template #' },
  { field: 'masterpro', label: 'Master Pro' },
  { field: 'billto_name', label: 'Bill To Name' },
  { field: 'sage_tag', label: 'Sage Tag' },
  { field: 'mas_tag', label: 'MAS Tag' },
  { field: 'deliver_to', label: 'Deliver To' },
  { field: 'quotenumber', label: 'Quote #' },
];

const DEFAULT_FIELD = 'pronumber';

/** Split pasted text on newlines/commas → a one-slot equality blob (matches any of
 *  the values via the backend's comma → IN). "" when empty. */
export function buildSearchBlob(field: string, raw: string): string {
  const values = (raw || '').split(/[\n,]+/).map((v) => v.trim()).filter(Boolean);
  if (!field || values.length === 0) return '';
  return `${field}~~=~~${values.join(',')}~~|||`;
}

/** Describe a search blob for the active-search banner: the field's label + its
 *  comma-joined values. Returns null for anything that isn't a search blob. */
export function describeSearchBlob(blob: string): { label: string; values: string } | null {
  const raw = (blob || '').split('|||').filter(Boolean)[0];
  if (!raw) return null;
  const [field = '', , values = ''] = raw.split('~~');
  if (!field) return null;
  const label = SEARCH_FIELDS.find((f) => f.field === field)?.label ?? field;
  return { label, values: values.split(',').map((v) => v.trim()).filter(Boolean).join(', ') };
}

interface Props {
  open: boolean;
  onClose: () => void;
  onApply: (blob: string) => void;
  /** Active search blob — prefilled for edit/re-run on reopen. */
  initialBlob?: string;
}

const TemplateSearchDialog: React.FC<Props> = ({ open, onClose, onApply, initialBlob }) => {
  const [field, setField] = useState(DEFAULT_FIELD);
  const [text, setText] = useState('');

  // On open, prefill from the active search (so the user can edit + re-run it);
  // a one-value-per-line textarea is easiest to tweak. Empty → fresh box.
  useEffect(() => {
    if (!open) return;
    const raw = (initialBlob || '').split('|||').filter(Boolean)[0];
    if (raw) {
      const [f = '', , values = ''] = raw.split('~~');
      setField(SEARCH_FIELDS.some((s) => s.field === f) ? f : DEFAULT_FIELD);
      setText(values.split(',').map((v) => v.trim()).filter(Boolean).join('\n'));
    } else {
      setField(DEFAULT_FIELD);
      setText('');
    }
  }, [open, initialBlob]);

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;

  const search = () => {
    const blob = buildSearchBlob(field, text);
    if (blob) { onApply(blob); onClose(); }
  };

  const inputCls = 'border border-slate-300 dark:border-slate-600 rounded px-1.5 py-1 text-[0.75rem] bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/45 backdrop-blur-sm p-4 overflow-y-auto"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        role="dialog"
        aria-label="Custom Search"
        className="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-full max-w-xl border border-slate-200 dark:border-slate-700"
      >
        <ModalHeader title="Custom Search" onClose={onClose} dragHandleProps={dragHandleProps} />
        <div className="px-4 py-3 flex flex-col gap-2">
          <p className="text-[0.75rem] text-slate-500 dark:text-slate-400">
            Paste values — one per line or comma-separated — then choose what to search by.
          </p>
          <textarea
            aria-label="Search values"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={10}
            className={`${inputCls} w-full resize-y`}
            placeholder={'T110\nT111\nT112'}
          />
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[0.75rem] font-semibold text-slate-600 dark:text-slate-300">Search By:</span>
            <select aria-label="Search By" className={inputCls} value={field} onChange={(e) => setField(e.target.value)}>
              {SEARCH_FIELDS.map((f) => <option key={f.field} value={f.field}>{f.label}</option>)}
            </select>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-[0.78125rem] font-medium text-slate-700 bg-white border border-slate-300 rounded dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600">Cancel</button>
          <button type="button" onClick={search} disabled={!text.trim()} className="px-4 py-1.5 text-[0.78125rem] font-semibold text-white bg-blue-600 border border-blue-700 rounded hover:bg-blue-700 disabled:opacity-40">Search</button>
        </div>
      </div>
    </div>
  );
};

export default TemplateSearchDialog;
